import { useGetEncounterById } from "@elation/api-client/src/stitch-web";
import { HeaderText } from "@src/components/general/HeaderText";
import { SectionHeader } from "@src/components/general/SectionHeader";
import { Text } from "@src/components/general/Text";
import {
  Flexbox,
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/components/layout/Flexbox";
import { MarkdownRenderer } from "@src/components/markdown/MarkdownRenderer";
import { Alert, AlertDescription, AlertTitle } from "@src/components/ui/alert";
import { Button } from "@src/components/ui/button";
import { Spinner } from "@src/components/ui/spinner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@src/components/ui/tabs";
import { useIsEncounterFinalizing } from "@src/features/encounters/state/encounterActor";
import { ActionItems } from "@src/pages/PatientDetail/pages/TodayPage/components/ActionItems/ActionItems";
import { Coding } from "@src/pages/PatientDetail/pages/TodayPage/components/Coding/Coding";
import { Copy, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { CompletedAgendaItems } from "./components/CompletedAgendaItems";

export interface PatientEncounterPageProps {
  patientId: string;
  encounterId: string;
}

export function PatientEncounterPage({
  patientId: _patientId,
  encounterId,
}: PatientEncounterPageProps) {
  const [visitNoteTab, setVisitNoteTab] = useState("soap");

  const encounterNumeric = useMemo(() => {
    const n = Number.parseInt(encounterId, 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [encounterId]);

  const {
    data: encounterData,
    isLoading,
    isError,
    error,
  } = useGetEncounterById(Number(encounterId), {
    query: {
      enabled: !!encounterId,
    },
  });

  const soapnoteText = encounterData?.data.note ?? "";
  const hasSoapBody = soapnoteText.trim().length > 0;

  const handleCopyNote = async () => {
    if (!hasSoapBody) return;
    try {
      await navigator.clipboard.writeText(soapnoteText);
    } catch {
      // Clipboard may be unavailable (permissions / non-secure context).
    }
  };

  const isEncounterFinalizing = useIsEncounterFinalizing();

  if (
    isEncounterFinalizing ||
    encounterData?.data.status === "finalizing" ||
    isLoading
  ) {
    return (
      <Flexbox
        align="center"
        justify="center"
        className="fixed inset-0 z-50 bg-background"
        aria-busy="true"
        aria-live="polite"
      >
        <HorizontalFlexbox
          alignItems="center"
          gap="md"
          className="rounded-lg border border-border/60 bg-card px-6 py-4 shadow-[0_4px_12px_rgba(0,0,0,0.1)]"
        >
          <Spinner size="lg" className="text-emerald-600" />
          <Text sizing="sm" fontWeight={500}>
            {isLoading ? "Loading..." : "Generating..."}
          </Text>
        </HorizontalFlexbox>
      </Flexbox>
    );
  }

  return (
    <>
      <VerticalFlexbox
        gap="md"
        className="min-w-0 flex-1 px-4 py-6 md:px-8 md:py-8 lg:h-full lg:min-h-0"
      >
        <HeaderText tag="h2" fontWeight={600}>
          Visit
        </HeaderText>
        <VerticalFlexbox gap="lg" className="flex-col lg:min-h-0 lg:flex-1">
          <Flexbox
            gap="lg"
            className="flex-col lg:min-h-0 lg:flex-1 lg:flex-row lg:items-stretch lg:gap-8"
          >
            <VerticalFlexbox
              gap="lg"
              className="min-w-0 lg:min-h-0 lg:max-w-xl lg:shrink-0 lg:basis-[42%]"
            >
              <VerticalFlexbox gap="sm" className="shrink-0">
                <SectionHeader>Completed agenda items</SectionHeader>
                {encounterNumeric != null ? (
                  <CompletedAgendaItems encounterId={encounterNumeric} />
                ) : (
                  <Text appearance="system">Invalid encounter id.</Text>
                )}
              </VerticalFlexbox>
              <VerticalFlexbox gap="sm" className="lg:min-h-0 lg:flex-1">
                <SectionHeader>Action items</SectionHeader>
                {encounterNumeric != null ? (
                  <ActionItems encounterId={encounterNumeric} />
                ) : (
                  <Text appearance="system">Invalid encounter id.</Text>
                )}
              </VerticalFlexbox>
            </VerticalFlexbox>
            <VerticalFlexbox
              gap="sm"
              className="min-w-0 lg:min-h-0 lg:min-w-0 lg:flex-1"
            >
              <SectionHeader>Visit note</SectionHeader>
              <Tabs
                value={visitNoteTab}
                onValueChange={setVisitNoteTab}
                className="flex flex-col gap-2 lg:min-h-0 lg:flex-1"
              >
                <HorizontalFlexbox
                  alignItems="center"
                  justifyContent="space-between"
                  gap="sm"
                  className="flex-wrap"
                >
                  <TabsList className="h-9 shrink-0">
                    <TabsTrigger value="soap">SOAP note</TabsTrigger>
                    <TabsTrigger value="scratchpad">Scratchpad</TabsTrigger>
                  </TabsList>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    disabled={!hasSoapBody}
                    aria-label="Copy SOAP note"
                    onClick={() => void handleCopyNote()}
                  >
                    <Copy className="size-4" />
                  </Button>
                </HorizontalFlexbox>
                <VerticalFlexbox
                  gap="xs"
                  className="rounded-lg border border-border bg-card lg:min-h-0 lg:flex-1 lg:overflow-hidden"
                >
                  <TabsContent
                    value="soap"
                    className="m-0 flex flex-col p-4 focus-visible:outline-none lg:min-h-0 lg:flex-1 lg:overflow-auto"
                  >
                    {encounterNumeric == null ? (
                      <Text appearance="system">Invalid encounter id.</Text>
                    ) : isLoading ? (
                      <HorizontalSpinner />
                    ) : isError ? (
                      <Text appearance="danger">
                        {error instanceof Error
                          ? error.message
                          : "Unable to load SOAP note."}
                      </Text>
                    ) : (
                      <>
                        {hasSoapBody ? (
                          <Alert className="mb-4 border-emerald-200/80 bg-emerald-50/90 text-emerald-950 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-50">
                            <Sparkles className="size-4 text-emerald-600 dark:text-emerald-300" />
                            <div>
                              <AlertTitle>Draft note</AlertTitle>
                              <AlertDescription>
                                AI-generated draft — review and edit as needed.
                              </AlertDescription>
                            </div>
                          </Alert>
                        ) : null}
                        <MarkdownRenderer
                          markdown={
                            hasSoapBody
                              ? soapnoteText
                              : "_No SOAP note is available for this encounter yet._"
                          }
                          className="prose prose-sm dark:prose-invert max-w-none"
                        />
                      </>
                    )}
                  </TabsContent>
                  <TabsContent
                    value="scratchpad"
                    className="m-0 flex flex-col p-4 focus-visible:outline-none lg:min-h-0 lg:flex-1 lg:overflow-auto"
                  >
                    <Text appearance="system" sizing="sm">
                      Scratchpad edits from the linked visit note will appear
                      here when available.
                    </Text>
                  </TabsContent>
                </VerticalFlexbox>
              </Tabs>
            </VerticalFlexbox>
          </Flexbox>
          <VerticalFlexbox gap="sm" className="border-t border-border pt-4">
            <Coding />
          </VerticalFlexbox>
        </VerticalFlexbox>
      </VerticalFlexbox>
    </>
  );
}

function HorizontalSpinner() {
  return (
    <Flexbox
      alignItems="center"
      justifyContent="center"
      className="min-h-[120px] flex-1"
    >
      <Spinner />
    </Flexbox>
  );
}
